/**
 * Installation Jobs Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchInstallationJobs,
  fetchInstallationJob,
  createInstallationJob,
  updateInstallationJob,
  markInstallationIqCreated,
  deleteInstallationJob,
  generateAccessCode,
  revokeAccessCode,
  sendAccessCode,
  fetchSiteSensors,
  fetchSensorsBySite,
  fetchAvailableAccountSensors,
  fetchAuditLogs,
  fetchTemplates,
  fetchTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  rejectInstallationJob,
  dispatchToFieldNation,
  syncFieldNationStatus,
} from '../api';
import { getQuoteByMongoId, getQuotes, getSensorsByQuote } from '../../quotes/api';
import type {
  JobsQueryParams,
  CreateJobInput,
  UpdateJobInput,
  InstallationJobTemplate,
} from '../types';

// =============================================================================
// Installation Jobs Hooks
// =============================================================================

export function useInstallationJobs(params: JobsQueryParams = {}) {
  return useQuery({
    queryKey: ['installation-jobs', params],
    queryFn: () => fetchInstallationJobs(params),
  });
}

export function useInstallationJob(id: string | undefined) {
  return useQuery({
    queryKey: ['installation-job', id],
    queryFn: () => fetchInstallationJob(id!),
    enabled: !!id,
  });
}

export function useCreateInstallationJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateJobInput) => createInstallationJob(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installation-jobs'] });
    },
  });
}

export function useUpdateInstallationJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateJobInput }) =>
      updateInstallationJob(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['installation-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['installation-job', data.id] });
    },
  });
}

export function useMarkInstallationIqCreated() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      jobId,
      sensorId,
      createdAt,
      createdReportId,
      createdReportNumber,
    }: {
      jobId: string;
      sensorId: string;
      createdAt: string;
      createdReportId: string;
      createdReportNumber: string;
    }) => markInstallationIqCreated(jobId, sensorId, {
      createdAt,
      createdReportId,
      createdReportNumber,
    }),
    onSuccess: (data) => {
      queryClient.setQueryData(['installation-job', data.id], data);
      queryClient.invalidateQueries({ queryKey: ['installation-jobs'] });
    },
  });
}

export function useRejectInstallationJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { reason: string; notifyEmail?: string } }) =>
      rejectInstallationJob(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['installation-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['installation-job', data.id] });
    },
  });
}

export function useDeleteInstallationJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteInstallationJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installation-jobs'] });
    },
  });
}

// =============================================================================
// Access Code Hooks
// =============================================================================

export function useGenerateAccessCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId, expiresInDays }: { jobId: string; expiresInDays?: number }) =>
      generateAccessCode(jobId, expiresInDays),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['installation-job', variables.jobId] });
    },
  });
}

export function useRevokeAccessCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId, codeHash }: { jobId: string; codeHash: string }) =>
      revokeAccessCode(jobId, codeHash),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['installation-job', variables.jobId] });
    },
  });
}

export function useSendAccessCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      jobId,
      codeId,
      method,
      recipient,
      code,
      language,
    }: {
      jobId: string;
      codeId: string;
      method: 'email' | 'sms';
      recipient: string;
      code?: string;
      language?: 'en' | 'fr';
    }) => sendAccessCode(jobId, codeId, method, recipient, code, language),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['installation-job', variables.jobId] });
    },
  });
}

// =============================================================================
// Audit Log Hooks
// =============================================================================

export function useAuditLogs(jobId: string | undefined, params: { pageNumber?: number; pageLength?: number } = {}) {
  return useQuery({
    queryKey: ['installation-job-audit-logs', jobId, params],
    queryFn: () => fetchAuditLogs(jobId!, params),
    enabled: !!jobId,
  });
}

// =============================================================================
// Site Sensors Hooks
// =============================================================================

export function useSiteSensors(jobId: string | undefined) {
  return useQuery({
    queryKey: ['installation-job-site-sensors', jobId],
    queryFn: () => fetchSiteSensors(jobId!),
    enabled: !!jobId,
  });
}

export function useSensorsBySite(mongoSiteId: string | undefined) {
  return useQuery({
    queryKey: ['sensors-by-site', mongoSiteId],
    queryFn: () => fetchSensorsBySite(mongoSiteId!),
    enabled: !!mongoSiteId,
  });
}

// =============================================================================
// Template Hooks
// =============================================================================

export function useTemplates() {
  return useQuery({
    queryKey: ['installation-job-templates'],
    queryFn: () => fetchTemplates(),
  });
}

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ['installation-job-template', id],
    queryFn: () => fetchTemplate(id!),
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Omit<InstallationJobTemplate, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) =>
      createTemplate(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installation-job-templates'] });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: Partial<Omit<InstallationJobTemplate, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>>;
    }) => updateTemplate(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['installation-job-templates'] });
      queryClient.invalidateQueries({ queryKey: ['installation-job-template', data.id] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installation-job-templates'] });
    },
  });
}

// =============================================================================
// Field Nation Hooks
// =============================================================================

export function useDispatchToFieldNation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => dispatchToFieldNation(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: ['installation-job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['installation-jobs'] });
    },
  });
}

export function useSyncFieldNationStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => syncFieldNationStatus(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: ['installation-job', jobId] });
    },
  });
}

// =============================================================================
// Quote Association Hooks
// =============================================================================

export function useQuoteByMongoId(mongoQuoteId: string | undefined | null) {
  return useQuery({
    queryKey: ['quote-by-mongo-id', mongoQuoteId],
    queryFn: () => getQuoteByMongoId(mongoQuoteId!),
    enabled: !!mongoQuoteId,
  });
}

export function useAccountQuotes(accountId: string | undefined | null) {
  return useQuery({
    queryKey: ['account-quotes', accountId],
    queryFn: () => getQuotes({ accountId: accountId!, pageLength: 100, sortKey: 'quoteNumber', sortDirection: -1 }),
    enabled: !!accountId,
  });
}

export function useSensorsByQuote(quoteNumber: string | undefined | null) {
  return useQuery({
    queryKey: ['sensors-by-quote', quoteNumber],
    queryFn: () => getSensorsByQuote(quoteNumber!),
    enabled: !!quoteNumber,
  });
}

export function useSensorsByAccount(accountId: string | undefined | null) {
  return useQuery({
    queryKey: ['sensors-by-account', accountId],
    queryFn: () => fetchAvailableAccountSensors(accountId!),
    enabled: !!accountId,
  });
}
